// canonicalReadiness — pre-commit signal for "does this canonical have
// the enrichment data the kitchen needs after commit?"
//
// Two axes the rest of the app depends on but that aren't part of
// classifyScan's tier check:
//
//   * NUTRITION — kcal / calories per 100g. Drives the per-row calorie
//     readout on item cards, monthly nutrition rollups, recipe macro
//     totals. Without it those surfaces render dashes.
//
//   * DENSITY / WEIGHT — either a density block (g_per_tsp etc.) for
//     volumetric ingredients or a count_weight_g for items sold by
//     count. Without it, unit conversions fail (recipes asking for
//     "1 cup flour" can't subtract from a "16 oz" bag) and the cook
//     mode chip swaps go silent.
//
// All reads route through getIngredientInfo so synthetic canonicals
// (in dbCanonicals only) and admin overlays both surface correctly —
// per the CLAUDE.md "single read entry point" rule.
//
// Pure function. Caller passes the dbInfo getter from useIngredientInfo
// so this stays React-free and reusable from commitTrip, scripts, etc.

import { findIngredient, getIngredientInfo } from "../data/ingredients";

/**
 * readinessFor(canonicalId, getInfo)
 *
 * @param {string|null} canonicalId
 * @param {(id: string) => object | null} getInfo  — from useIngredientInfo()
 * @returns {{
 *   canonicalId: string|null,
 *   hasNutrition: boolean,
 *   hasDensity: boolean,
 *   ready: boolean,
 *   missing: string[],
 * }}
 */
export function readinessFor(canonicalId, getInfo) {
  if (!canonicalId) {
    return {
      canonicalId: null,
      hasNutrition: false,
      hasDensity: false,
      ready: false,
      missing: ["canonical"],
    };
  }
  const ing = findIngredient(canonicalId);
  const dbInfo = typeof getInfo === "function" ? getInfo(canonicalId) : null;
  const info = getIngredientInfo(ing, dbInfo);

  const nutrition = info?.nutrition || null;
  const hasNutrition = !!nutrition && (
    Number.isFinite(Number(nutrition.kcal)) ||
    Number.isFinite(Number(nutrition.calories)) ||
    Number.isFinite(Number(nutrition.kcal_per_100g))
  );

  // Density — either a structured block or the legacy flat number,
  // OR a count-weight (eggs, apples, lemons sold by piece). Any of
  // these is enough to ground a unit conversion.
  const hasDensity = !!(
    info?.density ||
    info?.density_g_per_ml ||
    info?.count_weight_g
  );

  return {
    canonicalId,
    hasNutrition,
    hasDensity,
    ready: hasNutrition && hasDensity,
    missing: [
      !hasNutrition && "nutrition",
      !hasDensity && "density",
    ].filter(Boolean),
  };
}
